<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-shield-account me-2"></i>{{ $t('page.sgbv', 'SGBV / GBV Recovery Centre') }}</h4>
        </div>
        <div v-if="canAccess" class="d-flex gap-2">
          <button class="hms-btn hms-btn-success" @click="openRegisterModal()"><i class="mdi mdi-plus"></i> New Survivor</button>
          <button class="hms-btn hms-btn-blue" @click="loadAll"><i class="mdi mdi-refresh"></i></button>
        </div>
      </div>

      <div v-if="!canAccess" class="hms-card text-center" style="padding:40px; background:linear-gradient(135deg,#fef3c7,#fde68a); border:1px dashed #d97706;">
        <i class="mdi mdi-shield-alert-outline" style="font-size:48px; color:#b45309;"></i>
        <h4 class="mt-2" style="color:#78350f;">Clinical Access Only</h4>
        <p style="color:#92400e;">The SGBV register contains protected, sensitive information about survivors of gender-based violence. Access is restricted to clinical roles. This access attempt has been audit-logged.</p>
        <button class="hms-btn hms-btn-neutral" @click="$router.push('/dashboard')">Back to Dashboard</button>
      </div>
      <div v-else>

      <div class="hms-confidentiality-banner">
        <i class="mdi mdi-shield-lock"></i>
        <strong>CONFIDENTIAL.</strong> All access to this register is logged. Limit discussion to authorised GBV-RC staff.
      </div>

      <!-- STATS -->
      <div class="hms-stat-row">
        <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ stats.open_cases || 0 }}</div><div class="hms-stat-lbl">Open Cases</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-danger">{{ stats.pep_pending || 0 }}</div><div class="hms-stat-lbl">PEP pending (window open)</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ stats.ec_pending || 0 }}</div><div class="hms-stat-lbl">EC pending</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ stats.p3_pending || 0 }}</div><div class="hms-stat-lbl">P3 form pending</div></div>
        <div class="hms-stat"><div class="hms-stat-num">{{ stats.new_30d || 0 }}</div><div class="hms-stat-lbl">New (30d)</div></div>
      </div>

      <!-- TABS -->
      <div class="hms-tab-row">
        <button class="hms-tab" :class="{ active: tab==='cases' }" @click="tab='cases'"><i class="mdi mdi-clipboard-list me-1"></i>Cases</button>
        <button class="hms-tab" :class="{ active: tab==='detail' }" @click="tab='detail'" :disabled="!detail.case"><i class="mdi mdi-card-account-details me-1"></i>Case File</button>
        <button class="hms-tab" :class="{ active: tab==='indicators' }" @click="tab='indicators'; loadIndicators()"><i class="mdi mdi-chart-box me-1"></i>Indicators</button>
      </div>

      <!-- ============ CASES ============ -->
      <div v-if="tab==='cases'">
        <div class="d-flex gap-2 mb-2">
          <select v-model="statusFilter" class="hms-input" @change="loadCases" style="max-width:200px">
            <option value="open">Open</option>
            <option value="follow_up">Follow-up</option>
            <option value="closed_resolved">Resolved</option>
            <option value="closed_lost">Lost</option>
            <option value="court_referral">Court referral</option>
            <option value="">All</option>
          </select>
        </div>
        <table class="hms-table">
          <thead>
            <tr><th>Case #</th><th>Survivor</th><th>Presented</th><th>Hrs since</th><th>Type</th><th>PEP</th><th>EC</th><th>P3</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            <tr v-for="c in cases" :key="c.case_id" :class="rowClass(c)">
              <td><strong>{{ c.case_number }}</strong></td>
              <td>{{ c.patient_name }}<div class="hms-muted-sm">{{ c.patient_no }}</div></td>
              <td>{{ c.presentation_date }} {{ c.presentation_time ? c.presentation_time.substring(0,5) : '' }}</td>
              <td><strong :class="hoursClass(c.hours_since_incident)">{{ c.hours_since_incident != null ? c.hours_since_incident + 'h' : '—' }}</strong></td>
              <td>
                <span v-if="c.violence_sexual == 1" class="hms-tag hms-tag-danger">Sexual</span>
                <span v-if="c.violence_defilement == 1" class="hms-tag hms-tag-danger">Defilement</span>
                <span v-if="c.violence_physical == 1" class="hms-tag hms-tag-warn">Physical</span>
                <span v-if="c.violence_psychological == 1" class="hms-tag hms-tag-warn">Psych</span>
                <span v-if="c.violence_fgm == 1" class="hms-tag hms-tag-danger">FGM</span>
              </td>
              <td>
                <span v-if="c.pep_started == 1" class="hms-pill hms-pill-success">Started</span>
                <span v-else-if="c.pep_eligible == 1" class="hms-pill hms-pill-danger">PEP DUE</span>
                <span v-else class="hms-pill hms-pill-muted">N/A</span>
              </td>
              <td>
                <span v-if="c.ec_given == 1" class="hms-pill hms-pill-success">Given</span>
                <span v-else-if="c.ec_eligible == 1" class="hms-pill hms-pill-warn">EC DUE</span>
                <span v-else class="hms-pill hms-pill-muted">N/A</span>
              </td>
              <td>
                <span v-if="c.p3_form_completed == 1" class="hms-pill hms-pill-success">Done</span>
                <span v-else class="hms-pill hms-pill-warn">Pending</span>
              </td>
              <td><span class="hms-pill" :class="statusPill(c.status)">{{ c.status }}</span></td>
              <td><button class="hms-btn hms-btn-blue hms-btn-sm" @click="openDetail(c)"><i class="mdi mdi-folder-open"></i></button></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ============ DETAIL ============ -->
      <div v-if="tab==='detail' && detail.case">
        <div v-if="detail.case.pep_eligible == 1 && detail.case.pep_started == 0" class="hms-warn-banner mb-2">
          <i class="mdi mdi-alarm-light"></i>
          <strong>PEP WINDOW OPEN ({{ detail.case.hours_since_incident }}hr since incident, &lt;72hr cutoff)</strong> — initiate HIV PEP regimen now.
        </div>
        <div v-if="detail.case.ec_eligible == 1 && detail.case.ec_given == 0" class="hms-warn-banner hms-warn-banner-amber mb-2">
          <i class="mdi mdi-pill"></i>
          <strong>EC WINDOW OPEN ({{ detail.case.hours_since_incident }}hr, &lt;120hr cutoff)</strong> — give emergency contraception.
        </div>

        <div class="hms-card mb-3">
          <div class="hms-card-body">
            <div class="d-flex justify-content-between flex-wrap gap-3">
              <div>
                <div class="hms-name-big">{{ detail.case.patient_name }}</div>
                <div class="hms-muted-sm">{{ detail.case.case_number }} · {{ detail.case.patient_no }} · {{ detail.case.sex }} · DOB {{ detail.case.date_of_birth }}</div>
                <div class="hms-muted-sm">Presented: {{ detail.case.presentation_date }} {{ detail.case.presentation_time }} · {{ detail.case.hours_since_incident != null ? detail.case.hours_since_incident + 'h since incident' : 'incident time unknown' }}</div>
              </div>
              <div class="text-end">
                <div><span class="hms-pill" :class="statusPill(detail.case.status)">{{ detail.case.status }}</span></div>
                <div class="hms-muted-sm">Examined by: {{ detail.case.examined_by_name || '—' }}</div>
                <div class="hms-muted-sm">Chaperone: {{ detail.case.chaperone_name || '—' }}</div>
              </div>
            </div>

            <div class="hms-section-label mt-3">VIOLENCE TYPES</div>
            <div>
              <span v-if="detail.case.violence_sexual == 1" class="hms-tag hms-tag-danger">Sexual</span>
              <span v-if="detail.case.violence_defilement == 1" class="hms-tag hms-tag-danger">Defilement</span>
              <span v-if="detail.case.violence_physical == 1" class="hms-tag hms-tag-warn">Physical</span>
              <span v-if="detail.case.violence_psychological == 1" class="hms-tag hms-tag-warn">Psychological</span>
              <span v-if="detail.case.violence_economic == 1" class="hms-tag">Economic</span>
              <span v-if="detail.case.violence_fgm == 1" class="hms-tag hms-tag-danger">FGM</span>
              <span v-if="detail.case.violence_child_marriage == 1" class="hms-tag hms-tag-warn">Child marriage</span>
              <span v-if="detail.case.violence_other" class="hms-tag">{{ detail.case.violence_other }}</span>
              <span class="hms-muted-sm ms-2">Perpetrator: {{ detail.case.perpetrator_relationship }} ({{ detail.case.perpetrator_count }})</span>
            </div>

            <div class="hms-grid-2 mt-3">
              <div>
                <div class="hms-section-label">CLINICAL FINDINGS</div>
                <div v-if="detail.case.chief_complaint"><strong>CC:</strong> {{ detail.case.chief_complaint }}</div>
                <div v-if="detail.case.injuries_documented" class="hms-muted-sm mt-1"><strong>Injuries:</strong> {{ detail.case.injuries_documented }}</div>
                <div v-if="detail.case.genital_exam_findings" class="hms-muted-sm mt-1"><strong>Genital exam:</strong> {{ detail.case.genital_exam_findings }}</div>
                <div v-if="detail.case.evidence_collected" class="hms-muted-sm mt-1"><strong>Evidence:</strong> {{ detail.case.evidence_collected }}</div>
              </div>
              <div>
                <div class="hms-section-label">BASELINE LABS</div>
                <div class="hms-ind-row"><span>HIV</span><span class="hms-pill" :class="testPill(detail.case.hiv_test_baseline)">{{ detail.case.hiv_test_baseline }}</span></div>
                <div class="hms-ind-row"><span>Pregnancy</span><span class="hms-pill" :class="testPill(detail.case.pregnancy_test)">{{ detail.case.pregnancy_test }}</span></div>
                <div class="hms-ind-row"><span>Syphilis</span><span class="hms-pill" :class="testPill(detail.case.syphilis_baseline)">{{ detail.case.syphilis_baseline }}</span></div>
                <div class="hms-ind-row"><span>HepB</span><span class="hms-pill" :class="testPill(detail.case.hep_b_baseline)">{{ detail.case.hep_b_baseline }}</span></div>
              </div>
            </div>

            <div class="hms-grid-2 mt-3">
              <div>
                <div class="hms-section-label">INTERVENTIONS</div>
                <div class="hms-ind-row"><span>HIV PEP</span><strong>{{ detail.case.pep_started == 1 ? '✓ ' + (detail.case.pep_regimen || 'started') : (detail.case.pep_eligible ? 'eligible — pending' : 'not eligible') }}</strong></div>
                <div class="hms-ind-row"><span>Emergency contraception</span><strong>{{ detail.case.ec_given == 1 ? '✓ ' + (detail.case.ec_drug || 'given') : (detail.case.ec_eligible ? 'eligible — pending' : 'not eligible') }}</strong></div>
                <div class="hms-ind-row"><span>STI prophylaxis</span><strong>{{ detail.case.sti_prophylaxis == 1 ? '✓ ' + (detail.case.sti_drugs || 'given') : 'not given' }}</strong></div>
                <div class="hms-ind-row"><span>HepB vaccine</span><strong>{{ detail.case.hep_b_vaccinated == 1 ? '✓' : '—' }}</strong></div>
                <div class="hms-ind-row"><span>Tetanus toxoid</span><strong>{{ detail.case.tetanus_toxoid == 1 ? '✓' : '—' }}</strong></div>
              </div>
              <div>
                <div class="hms-section-label">LEGAL / SOCIAL</div>
                <div class="hms-ind-row"><span>P3 form</span><strong>{{ detail.case.p3_form_completed == 1 ? '✓ ' + (detail.case.p3_form_no || '') : 'pending' }}</strong></div>
                <div class="hms-ind-row"><span>Police report</span><strong>{{ detail.case.police_report_made == 1 ? '✓ OB ' + (detail.case.police_ob_no || '') : '—' }}</strong></div>
                <div class="hms-ind-row"><span>Police station</span><strong>{{ detail.case.police_station || '—' }}</strong></div>
                <div class="hms-ind-row"><span>Mental health referral</span><strong>{{ detail.case.mh_referral == 1 ? '✓' : '—' }}</strong></div>
                <div class="hms-ind-row"><span>Safe house</span><strong>{{ detail.case.safe_house_referral == 1 ? '✓ ' + (detail.case.safe_house_name || '') : '—' }}</strong></div>
              </div>
            </div>

            <div class="d-flex gap-2 mt-3 flex-wrap">
              <button class="hms-btn hms-btn-success hms-btn-sm" @click="openInterventionsModal()"><i class="mdi mdi-medical-bag"></i> Update Interventions</button>
              <button class="hms-btn hms-btn-amber hms-btn-sm" @click="openLegalModal()"><i class="mdi mdi-gavel"></i> Legal / Social</button>
              <button class="hms-btn hms-btn-blue hms-btn-sm" @click="openFollowupModal()"><i class="mdi mdi-clipboard-text"></i> Record Follow-up</button>
              <button class="hms-btn hms-btn-secondary hms-btn-sm" @click="openCloseModal()"><i class="mdi mdi-flag-checkered"></i> Close Case</button>
            </div>
          </div>
        </div>

        <div class="hms-card">
          <div class="hms-card-head">Follow-up visits ({{ detail.followups.length }})</div>
          <div class="hms-card-body p-0">
            <table v-if="detail.followups.length" class="hms-table">
              <thead><tr><th>Date</th><th>Type</th><th>PEP adherence</th><th>HIV retest</th><th>PHQ-9</th><th>PTSD</th><th>By</th></tr></thead>
              <tbody>
                <tr v-for="f in detail.followups" :key="f.followup_id">
                  <td>{{ f.visit_date }}</td>
                  <td><span class="hms-pill hms-pill-info">{{ f.visit_type }}</span></td>
                  <td><span class="hms-pill" :class="adherencePill(f.pep_adherence)">{{ f.pep_adherence }}</span></td>
                  <td><span class="hms-pill" :class="testPill(f.hiv_retest_result)">{{ f.hiv_retest_result }}</span></td>
                  <td>{{ f.mh_screen_phq9 != null ? f.mh_screen_phq9 + '/27' : '—' }}</td>
                  <td>{{ f.mh_screen_ptsd != null ? f.mh_screen_ptsd + '/80' : '—' }}</td>
                  <td>{{ f.recorded_by_name || '—' }}</td>
                </tr>
              </tbody>
            </table>
            <div v-else class="hms-pad text-center hms-muted">No follow-ups yet.</div>
          </div>
        </div>
      </div>

      <!-- ============ INDICATORS ============ -->
      <div v-if="tab==='indicators'">
        <div class="d-flex gap-2 mb-3">
          <select v-model="indicatorPeriod" class="hms-input" @change="loadIndicators" style="max-width:200px">
            <option value="this_month">This month</option>
            <option value="this_quarter">This quarter</option>
            <option value="this_year">This year</option>
          </select>
        </div>
        <div class="hms-grid-2 mb-3">
          <div class="hms-card">
            <div class="hms-card-head">Case volume by violence type</div>
            <div class="hms-card-body">
              <div class="hms-ind-row"><span>Total cases</span><strong>{{ indicators.Indicators && indicators.Indicators.cases_total || 0 }}</strong></div>
              <div class="hms-ind-row"><span>Sexual</span><strong>{{ indicators.Indicators && indicators.Indicators.sexual || 0 }}</strong></div>
              <div class="hms-ind-row"><span>Defilement</span><strong>{{ indicators.Indicators && indicators.Indicators.defilement || 0 }}</strong></div>
              <div class="hms-ind-row"><span>Physical</span><strong>{{ indicators.Indicators && indicators.Indicators.physical || 0 }}</strong></div>
              <div class="hms-ind-row"><span>Psychological</span><strong>{{ indicators.Indicators && indicators.Indicators.psychological || 0 }}</strong></div>
              <div class="hms-ind-row"><span>FGM</span><strong>{{ indicators.Indicators && indicators.Indicators.fgm || 0 }}</strong></div>
              <div class="hms-ind-row"><span>Child marriage</span><strong>{{ indicators.Indicators && indicators.Indicators.child_marriage || 0 }}</strong></div>
            </div>
          </div>
          <div class="hms-card">
            <div class="hms-card-head">Service coverage</div>
            <div class="hms-card-body">
              <div class="hms-ind-row"><span>PEP eligible</span><strong>{{ indicators.Indicators && indicators.Indicators.pep_eligible || 0 }}</strong></div>
              <div class="hms-ind-row"><span>PEP started</span><strong>{{ indicators.Indicators && indicators.Indicators.pep_started || 0 }} <span class="hms-muted-sm">({{ indicators.PepInitiationRate || 0 }}%)</span></strong></div>
              <div class="hms-ind-row"><span>EC eligible</span><strong>{{ indicators.Indicators && indicators.Indicators.ec_eligible || 0 }}</strong></div>
              <div class="hms-ind-row"><span>EC given</span><strong>{{ indicators.Indicators && indicators.Indicators.ec_given || 0 }} <span class="hms-muted-sm">({{ indicators.EcInitiationRate || 0 }}%)</span></strong></div>
              <div class="hms-ind-row"><span>P3 form completed</span><strong>{{ indicators.Indicators && indicators.Indicators.p3_completed || 0 }}</strong></div>
              <div class="hms-ind-row"><span>Police reports filed</span><strong>{{ indicators.Indicators && indicators.Indicators.police_reported || 0 }}</strong></div>
              <div class="hms-ind-row"><span>Mental health referrals</span><strong>{{ indicators.Indicators && indicators.Indicators.mh_referred || 0 }}</strong></div>
              <div class="hms-ind-row"><span>Safe house referrals</span><strong>{{ indicators.Indicators && indicators.Indicators.safe_house || 0 }}</strong></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== REGISTER MODAL ===== -->
    <div v-if="registerModal" class="hms-modal-bg" @click.self="registerModal=false">
      <div class="hms-modal" style="width:820px">
        <div class="hms-modal-head"><h5>Register SGBV / GBV Case</h5><button class="hms-icon-btn" @click="registerModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="hms-confidentiality-banner mb-2"><i class="mdi mdi-shield-lock"></i> Ensure consent obtained and chaperone present before exam.</div>
          <div v-if="!form.patient_id">
            <label class="hms-label">Survivor (patient) *</label>
            <div class="d-flex gap-2">
              <input v-model="form.patient_search" class="hms-input" placeholder="Name / patient #" @keyup.enter="findPatient" />
              <button class="hms-btn hms-btn-blue" @click="findPatient">Find</button>
            </div>
          </div>
          <div v-else class="hms-patient-chip"><i class="mdi mdi-account"></i> {{ form.patient_name }} <a href="javascript:void(0)" class="ms-2" @click="form.patient_id=null">change</a></div>
          <div class="row g-2 mt-2">
            <div class="col-3"><label class="hms-label">Presented *</label><input v-model="form.presentation_date" type="date" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Time</label><input v-model="form.presentation_time" type="time" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Incident date</label><input v-model="form.incident_date" type="date" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Incident time</label><input v-model="form.incident_time" type="time" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Incident location</label><input v-model="form.incident_location" class="hms-input" /></div>

            <div class="col-12"><div class="hms-section-label">VIOLENCE TYPES (tick all that apply)</div></div>
            <div class="col-3"><label><input type="checkbox" v-model="form.violence_sexual" :true-value="1" :false-value="0" /> Sexual</label></div>
            <div class="col-3"><label><input type="checkbox" v-model="form.violence_defilement" :true-value="1" :false-value="0" /> Defilement</label></div>
            <div class="col-3"><label><input type="checkbox" v-model="form.violence_physical" :true-value="1" :false-value="0" /> Physical</label></div>
            <div class="col-3"><label><input type="checkbox" v-model="form.violence_psychological" :true-value="1" :false-value="0" /> Psychological</label></div>
            <div class="col-3"><label><input type="checkbox" v-model="form.violence_economic" :true-value="1" :false-value="0" /> Economic</label></div>
            <div class="col-3"><label><input type="checkbox" v-model="form.violence_fgm" :true-value="1" :false-value="0" /> FGM</label></div>
            <div class="col-3"><label><input type="checkbox" v-model="form.violence_child_marriage" :true-value="1" :false-value="0" /> Child marriage</label></div>
            <div class="col-3"><input v-model="form.violence_other" class="hms-input" placeholder="Other (specify)" /></div>

            <div class="col-12"><div class="hms-section-label">PERPETRATOR & REFERRAL</div></div>
            <div class="col-4"><label class="hms-label">Perpetrator relationship</label>
              <select v-model="form.perpetrator_relationship" class="hms-input">
                <option value="intimate_partner">Intimate partner</option><option value="family_member">Family member</option>
                <option value="known">Known</option><option value="stranger">Stranger</option>
                <option value="unknown">Unknown</option><option value="authority_figure">Authority figure</option>
                <option value="employer">Employer</option><option value="teacher">Teacher</option><option value="other">Other</option>
              </select>
            </div>
            <div class="col-2"><label class="hms-label">Number</label><input v-model.number="form.perpetrator_count" type="number" min="1" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Referral source</label>
              <select v-model="form.referral_source" class="hms-input">
                <option value="self">Self</option><option value="police">Police</option>
                <option value="school">School</option><option value="NGO">NGO</option>
                <option value="hospital">Hospital</option><option value="family">Family</option>
                <option value="community">Community</option><option value="court">Court</option><option value="other">Other</option>
              </select>
            </div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="form.consent_obtained" :true-value="1" :false-value="0" /> Consent obtained *</label></div>
            <div class="col-6"><label class="hms-label">Chaperone name *</label><input v-model="form.chaperone_name" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Examined by (user ID)</label><input v-model.number="form.examined_by" type="number" class="hms-input" /></div>

            <div class="col-12"><div class="hms-section-label">CLINICAL FINDINGS</div></div>
            <div class="col-12"><label class="hms-label">Chief complaint</label><textarea v-model="form.chief_complaint" class="hms-input" rows="2"></textarea></div>
            <div class="col-12"><label class="hms-label">Injuries documented</label><textarea v-model="form.injuries_documented" class="hms-input" rows="2"></textarea></div>
            <div class="col-12"><label class="hms-label">Genital exam findings (sexual violence cases)</label><textarea v-model="form.genital_exam_findings" class="hms-input" rows="2"></textarea></div>
            <div class="col-12"><label class="hms-label">Evidence collected</label><input v-model="form.evidence_collected" class="hms-input" placeholder="swabs, clothing, photos" /></div>
            <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="form.notes" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="registerModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving || !form.patient_id || !form.consent_obtained" @click="saveRegister">{{ saving?'Registering…':'Register Case' }}</button>
        </div>
      </div>
    </div>

    <!-- ===== INTERVENTIONS MODAL ===== -->
    <div v-if="interventionsModal" class="hms-modal-bg" @click.self="interventionsModal=false">
      <div class="hms-modal" style="width:680px">
        <div class="hms-modal-head"><h5>Update Interventions</h5><button class="hms-icon-btn" @click="interventionsModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-12"><div class="hms-section-label">BASELINE LABS</div></div>
            <div class="col-3"><label class="hms-label">HIV</label>
              <select v-model="upd.hiv_test_baseline" class="hms-input"><option value="not_done">Not done</option><option value="negative">Negative</option><option value="positive">Positive</option><option value="indeterminate">Indeterminate</option><option value="declined">Declined</option></select>
            </div>
            <div class="col-3"><label class="hms-label">Pregnancy</label>
              <select v-model="upd.pregnancy_test" class="hms-input"><option value="not_done">Not done</option><option value="negative">Negative</option><option value="positive">Positive</option><option value="not_applicable">N/A</option></select>
            </div>
            <div class="col-3"><label class="hms-label">Syphilis</label>
              <select v-model="upd.syphilis_baseline" class="hms-input"><option value="not_done">Not done</option><option value="negative">Negative</option><option value="positive">Positive</option></select>
            </div>
            <div class="col-3"><label class="hms-label">HepB</label>
              <select v-model="upd.hep_b_baseline" class="hms-input"><option value="not_done">Not done</option><option value="negative">Negative</option><option value="positive">Positive</option></select>
            </div>

            <div class="col-12"><div class="hms-section-label">PEP / EC / PROPHYLAXIS</div></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="upd.pep_started" :true-value="1" :false-value="0" /> PEP started</label></div>
            <div class="col-6"><label class="hms-label">PEP regimen</label><input v-model="upd.pep_regimen" class="hms-input" placeholder="TDF/3TC/DTG" /></div>
            <div class="col-3"><label class="hms-label">PEP start</label><input v-model="upd.pep_start_datetime" type="datetime-local" class="hms-input" /></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="upd.ec_given" :true-value="1" :false-value="0" /> EC given</label></div>
            <div class="col-9"><label class="hms-label">EC drug</label><input v-model="upd.ec_drug" class="hms-input" placeholder="Postinor-2 / Levonorgestrel 1.5mg / Cu-IUCD" /></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="upd.sti_prophylaxis" :true-value="1" :false-value="0" /> STI prophylaxis</label></div>
            <div class="col-9"><label class="hms-label">STI drugs</label><input v-model="upd.sti_drugs" class="hms-input" placeholder="Azithromycin 1g + Ceftriaxone 250mg + Metronidazole 2g" /></div>
            <div class="col-6 d-flex align-items-end"><label><input type="checkbox" v-model="upd.hep_b_vaccinated" :true-value="1" :false-value="0" /> HepB vaccinated</label></div>
            <div class="col-6 d-flex align-items-end"><label><input type="checkbox" v-model="upd.tetanus_toxoid" :true-value="1" :false-value="0" /> Tetanus toxoid given</label></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="interventionsModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveUpdate">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

    <!-- ===== LEGAL / SOCIAL MODAL ===== -->
    <div v-if="legalModal" class="hms-modal-bg" @click.self="legalModal=false">
      <div class="hms-modal" style="width:560px">
        <div class="hms-modal-head"><h5>Legal &amp; Social</h5><button class="hms-icon-btn" @click="legalModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="upd.p3_form_completed" :true-value="1" :false-value="0" /> P3 form done</label></div>
            <div class="col-9"><label class="hms-label">P3 form #</label><input v-model="upd.p3_form_no" class="hms-input" /></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="upd.police_report_made" :true-value="1" :false-value="0" /> Police report</label></div>
            <div class="col-4"><label class="hms-label">OB #</label><input v-model="upd.police_ob_no" class="hms-input" /></div>
            <div class="col-5"><label class="hms-label">Police station</label><input v-model="upd.police_station" class="hms-input" /></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="upd.court_referral" :true-value="1" :false-value="0" /> Court referral</label></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="upd.mh_referral" :true-value="1" :false-value="0" /> MH referral</label></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="upd.safe_house_referral" :true-value="1" :false-value="0" /> Safe house</label></div>
            <div class="col-12"><label class="hms-label">Safe house name</label><input v-model="upd.safe_house_name" class="hms-input" /></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="legalModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveUpdate">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

    <!-- ===== FOLLOWUP MODAL ===== -->
    <div v-if="followupModal" class="hms-modal-bg" @click.self="followupModal=false">
      <div class="hms-modal" style="width:600px">
        <div class="hms-modal-head"><h5>Record Follow-up</h5><button class="hms-icon-btn" @click="followupModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-4"><label class="hms-label">Date *</label><input v-model="followup.visit_date" type="date" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Visit type</label>
              <select v-model="followup.visit_type" class="hms-input">
                <option value="week_2">Week 2</option><option value="week_6">Week 6</option>
                <option value="month_3">Month 3</option><option value="month_6">Month 6</option>
                <option value="prn">PRN</option><option value="other">Other</option>
              </select>
            </div>
            <div class="col-4"><label class="hms-label">Next visit</label><input v-model="followup.next_visit_date" type="date" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">PEP adherence</label>
              <select v-model="followup.pep_adherence" class="hms-input"><option value="na">N/A</option><option value="good">Good</option><option value="fair">Fair</option><option value="poor">Poor</option><option value="discontinued">Discontinued</option></select>
            </div>
            <div class="col-8"><label class="hms-label">PEP side effects</label><input v-model="followup.pep_side_effects" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">HIV retest</label>
              <select v-model="followup.hiv_retest_result" class="hms-input"><option value="not_done">Not done</option><option value="negative">Negative</option><option value="positive">Positive</option><option value="indeterminate">Indeterminate</option></select>
            </div>
            <div class="col-4"><label class="hms-label">Pregnancy retest</label>
              <select v-model="followup.pregnancy_retest" class="hms-input"><option value="not_done">Not done</option><option value="negative">Negative</option><option value="positive">Positive</option></select>
            </div>
            <div class="col-4"><label class="hms-label">Syphilis retest</label>
              <select v-model="followup.syphilis_retest" class="hms-input"><option value="not_done">Not done</option><option value="negative">Negative</option><option value="positive">Positive</option></select>
            </div>
            <div class="col-4"><label class="hms-label">PHQ-9</label><input v-model.number="followup.mh_screen_phq9" type="number" min="0" max="27" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">PCL-5 (PTSD 0-80)</label><input v-model.number="followup.mh_screen_ptsd" type="number" min="0" max="80" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Findings</label><textarea v-model="followup.findings" class="hms-input" rows="2"></textarea></div>
            <div class="col-12"><label class="hms-label">Plan</label><textarea v-model="followup.plan" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="followupModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveFollowup">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

    <!-- ===== CLOSE CASE MODAL ===== -->
    <div v-if="closeModal" class="hms-modal-bg" @click.self="closeModal=false">
      <div class="hms-modal" style="width:480px">
        <div class="hms-modal-head"><h5>Close Case</h5><button class="hms-icon-btn" @click="closeModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <label class="hms-label">Final status *</label>
          <select v-model="upd.status" class="hms-input">
            <option value="closed_resolved">Closed — resolved</option>
            <option value="closed_lost">Closed — lost to follow-up</option>
            <option value="court_referral">Court referral</option>
            <option value="transferred">Transferred</option>
          </select>
          <label class="hms-label mt-2">Closure notes</label>
          <textarea v-model="upd.closure_notes" class="hms-input" rows="3"></textarea>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="closeModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveUpdate">{{ saving?'Saving…':'Close Case' }}</button>
        </div>
      </div>
    </div>

    </div> <!-- /canAccess v-else -->

  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "SgbvRegister",
  components: { Navigation },
  data() {
    return {
      tab: 'cases', saving: false,
      canAccess: false,
      stats: {},
      cases: [], statusFilter: 'open',
      detail: { case: null, followups: [] },

      indicatorPeriod: 'this_month',
      indicators: {},

      registerModal: false, form: this.blankForm(),
      interventionsModal: false, legalModal: false, closeModal: false,
      upd: this.blankUpd(),
      followupModal: false, followup: this.blankFollowup()
    };
  },
  async mounted() {
    try {
      var r = await executeJson({ function: 'canAccessSensitiveRegister', module: 'sgbv' }, hms);
      this.canAccess = !!(r.data && r.data.success && r.data.data && r.data.data.can);
    } catch (e) { this.canAccess = false; }
    if (this.canAccess) this.loadAll();
  },
  methods: {
    blankForm() {
      const today = new Date();
      const isoDate = today.toISOString().substring(0,10);
      const isoTime = today.toTimeString().substring(0,5);
      return {
        patient_id: null, patient_name: '', patient_search: '',
        presentation_date: isoDate, presentation_time: isoTime,
        incident_date: '', incident_time: '', incident_location: '',
        violence_sexual: 0, violence_defilement: 0, violence_physical: 0, violence_psychological: 0,
        violence_economic: 0, violence_fgm: 0, violence_child_marriage: 0, violence_other: '',
        perpetrator_relationship: 'unknown', perpetrator_count: 1,
        referral_source: 'self', consent_obtained: 0, chaperone_name: '', examined_by: null,
        chief_complaint: '', injuries_documented: '', genital_exam_findings: '', evidence_collected: '',
        notes: ''
      };
    },
    blankUpd() {
      return {
        hiv_test_baseline: 'not_done', pregnancy_test: 'not_done', syphilis_baseline: 'not_done', hep_b_baseline: 'not_done',
        pep_started: 0, pep_regimen: '', pep_start_datetime: '',
        ec_given: 0, ec_drug: '', sti_prophylaxis: 0, sti_drugs: '',
        hep_b_vaccinated: 0, tetanus_toxoid: 0,
        p3_form_completed: 0, p3_form_no: '', police_report_made: 0, police_ob_no: '', police_station: '',
        court_referral: 0, mh_referral: 0, safe_house_referral: 0, safe_house_name: '',
        status: 'closed_resolved', closure_notes: ''
      };
    },
    blankFollowup() {
      return {
        visit_date: new Date().toISOString().substring(0,10), visit_type: 'week_2',
        pep_adherence: 'na', pep_side_effects: '',
        hiv_retest_result: 'not_done', pregnancy_retest: 'not_done', syphilis_retest: 'not_done',
        mh_screen_phq9: null, mh_screen_ptsd: null,
        findings: '', plan: '', next_visit_date: ''
      };
    },

    async loadAll() { await this.loadCases(); },
    async loadCases() {
      try {
        const r = await executeJson({ function: 'getSgbvCases', status: this.statusFilter }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.cases = d.Cases || [];
          this.stats = d.Stats || {};
        }
      } catch (e) { console.error(e); }
    },

    openRegisterModal() { this.form = this.blankForm(); this.registerModal = true; },
    async findPatient() {
      const q = (this.form.patient_search || '').trim();
      if (!q) return;
      const r = await executeJson({ function: 'getPatients', search: q, limit: 1 }, hms);
      if (r.data && r.data.success) {
        const p = ((r.data.data && (r.data.data.Patients || r.data.data.patients)) || [])[0];
        if (!p) { alert('No patient found.'); return; }
        this.form.patient_id = p.patient_id;
        this.form.patient_name = p.first_name + ' ' + p.last_name;
      }
    },
    async saveRegister() {
      this.saving = true;
      try {
        const params = { function: 'registerSgbvCase', ...this.form };
        delete params.patient_search; delete params.patient_name;
        const r = await executeJson(params, hms);
        if (r.data && r.data.success) { this.registerModal = false; await this.loadCases(); alert(r.data.message); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    async openDetail(c) {
      try {
        const r = await executeJson({ function: 'getSgbvCaseDetail', case_id: c.case_id }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.detail = { case: d.Case, followups: d.Followups || [] };
          this.tab = 'detail';
        }
      } catch (e) { console.error(e); }
    },

    openInterventionsModal() {
      const c = this.detail.case;
      this.upd = { ...this.blankUpd(),
        hiv_test_baseline: c.hiv_test_baseline, pregnancy_test: c.pregnancy_test,
        syphilis_baseline: c.syphilis_baseline, hep_b_baseline: c.hep_b_baseline,
        pep_started: c.pep_started, pep_regimen: c.pep_regimen || '', pep_start_datetime: c.pep_start_datetime || '',
        ec_given: c.ec_given, ec_drug: c.ec_drug || '',
        sti_prophylaxis: c.sti_prophylaxis, sti_drugs: c.sti_drugs || '',
        hep_b_vaccinated: c.hep_b_vaccinated, tetanus_toxoid: c.tetanus_toxoid
      };
      this.interventionsModal = true;
    },
    openLegalModal() {
      const c = this.detail.case;
      this.upd = { ...this.blankUpd(),
        p3_form_completed: c.p3_form_completed, p3_form_no: c.p3_form_no || '',
        police_report_made: c.police_report_made, police_ob_no: c.police_ob_no || '', police_station: c.police_station || '',
        court_referral: c.court_referral, mh_referral: c.mh_referral, safe_house_referral: c.safe_house_referral, safe_house_name: c.safe_house_name || ''
      };
      this.legalModal = true;
    },
    openCloseModal() { this.upd = { ...this.blankUpd(), status: 'closed_resolved', closure_notes: '' }; this.closeModal = true; },
    async saveUpdate() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'updateSgbvCase', case_id: this.detail.case.case_id, ...this.upd }, hms);
        if (r.data && r.data.success) {
          this.interventionsModal = false; this.legalModal = false; this.closeModal = false;
          await this.openDetail(this.detail.case); await this.loadCases();
        } else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    openFollowupModal() { this.followup = this.blankFollowup(); this.followupModal = true; },
    async saveFollowup() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'recordSgbvFollowup', case_id: this.detail.case.case_id, ...this.followup }, hms);
        if (r.data && r.data.success) { this.followupModal = false; await this.openDetail(this.detail.case); }
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    async loadIndicators() {
      try {
        const r = await executeJson({ function: 'getSgbvIndicators', period: this.indicatorPeriod }, hms);
        if (r.data && r.data.success) this.indicators = r.data.data || {};
      } catch (e) { console.error(e); }
    },

    rowClass(c) {
      if (c.pep_eligible == 1 && c.pep_started == 0) return 'row-action';
      if (c.ec_eligible == 1 && c.ec_given == 0) return 'row-warn';
      if (c.violence_sexual == 1 || c.violence_defilement == 1) return 'row-warn';
      return '';
    },
    hoursClass(h) { if (h == null) return ''; if (h <= 72) return 'st-bad'; if (h <= 120) return 'st-warn'; return ''; },
    statusPill(s) { return ({ open: 'hms-pill-warn', follow_up: 'hms-pill-info', closed_resolved: 'hms-pill-success', closed_lost: 'hms-pill-muted', court_referral: 'hms-pill-info', transferred: 'hms-pill-muted' })[s] || ''; },
    testPill(t) { return ({ negative: 'hms-pill-success', positive: 'hms-pill-danger', indeterminate: 'hms-pill-warn', declined: 'hms-pill-muted', not_done: 'hms-pill-muted', not_applicable: 'hms-pill-muted' })[t] || ''; },
    adherencePill(a) { return ({ good: 'hms-pill-success', fair: 'hms-pill-warn', poor: 'hms-pill-danger', discontinued: 'hms-pill-danger', na: 'hms-pill-muted' })[a] || ''; }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }

.hms-confidentiality-banner { background: #fff5f5; border: 2px solid #fc8181; color: #742a2a; padding: 10px 14px; border-radius: 6px; margin-bottom: 12px; font-size: 13px; display: flex; align-items: center; gap: 8px; }
.hms-confidentiality-banner .mdi { font-size: 18px; }

.hms-stat-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 16px; }
.hms-stat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; text-align: center; }
.hms-stat-num { font-size: 26px; font-weight: 700; color: #1a202c; }
.hms-stat-lbl { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .5px; margin-top: 2px; }
.hms-stat-info { color: #2c5282; } .hms-stat-warning { color: #c05621; } .hms-stat-danger { color: #c53030; }

.hms-tab-row { display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin-bottom: 16px; }
.hms-tab { padding: 10px 16px; border: none; background: none; cursor: pointer; color: #718096; border-bottom: 2px solid transparent; font-size: 14px; }
.hms-tab.active { color: #2b6cb0; border-bottom-color: #2b6cb0; font-weight: 600; }
.hms-tab:disabled { opacity: .4; }

.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; }
.hms-card-head { padding: 10px 14px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #2d3748; }
.hms-card-body { padding: 14px; }
.hms-card-body.p-0 { padding: 0; }
.hms-grid-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }

.hms-name-big { font-size: 20px; font-weight: 700; color: #1a202c; }

.hms-section-label { font-size: 11px; font-weight: 700; color: #4a5568; letter-spacing: 1px; padding: 4px 0; border-bottom: 1px solid #edf2f7; margin: 8px 0 6px; }

.hms-warn-banner { background: #fff5f5; border: 2px solid #e53e3e; color: #742a2a; padding: 12px 16px; border-radius: 8px; font-size: 14px; display: flex; align-items: center; gap: 8px; }
.hms-warn-banner.hms-warn-banner-amber { background: #fffaf0; border-color: #dd6b20; color: #7b341e; }
.hms-warn-banner .mdi { font-size: 20px; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 13px; background: #fff; }
.hms-table th { background: #f7fafc; padding: 9px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.hms-table td { padding: 8px 9px; border-bottom: 1px solid #edf2f7; vertical-align: middle; }
.hms-table tr.row-warn   { background: #fffaf0; }
.hms-table tr.row-action { background: #fff5f5; }

.hms-input { width: 100%; padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-label { font-size: 11px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 3px; }

.hms-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.hms-pill-success { background: #c6f6d5; color: #22543d; }
.hms-pill-info    { background: #bee3f8; color: #2a4365; }
.hms-pill-warn    { background: #feebc8; color: #7b341e; }
.hms-pill-danger  { background: #fed7d7; color: #742a2a; }
.hms-pill-muted   { background: #edf2f7; color: #4a5568; }
.hms-tag { display: inline-block; padding: 2px 6px; border-radius: 6px; font-size: 10px; font-weight: 600; margin-right: 3px; background: #edf2f7; color: #4a5568; }
.hms-tag-warn { background: #feebc8; color: #7b341e; }
.hms-tag-danger { background: #fed7d7; color: #742a2a; }

.hms-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
.hms-modal { background: #fff; border-radius: 8px; max-height: 92vh; display: flex; flex-direction: column; }
.hms-modal-head { padding: 14px 18px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.hms-modal-body { padding: 16px 18px; overflow-y: auto; }
.hms-modal-foot { padding: 12px 18px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }
.hms-icon-btn { background: none; border: none; cursor: pointer; }
.hms-patient-chip { display: inline-flex; align-items: center; gap: 4px; background: #ebf8ff; color: #2c5282; padding: 6px 10px; border-radius: 6px; margin-top: 6px; font-size: 13px; }

.hms-ind-row { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid #edf2f7; font-size: 13px; }
.hms-ind-row strong { color: #2d3748; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-sm { padding: 4px 10px; font-size: 12px; }
.hms-btn-blue      { background: #2b6cb0; color: #fff; }
.hms-btn-success   { background: #38a169; color: #fff; }
.hms-btn-amber     { background: #d69e2e; color: #fff; }
.hms-btn-secondary { background: #fff; color: #2d3748; border: 1px solid #cbd5e0; }
.hms-btn-link      { background: transparent; color: #2b6cb0; border: none; cursor: pointer; }

.st-bad  { color: #c53030; font-weight: 700; }
.st-warn { color: #b45309; font-weight: 600; }
.hms-muted { color: #a0aec0; }
.hms-muted-sm { color: #a0aec0; font-size: 11px; }
.hms-pad { padding: 24px; }
.ms-2 { margin-left: 8px; }
</style>
